import { useCallback } from 'react';
import { useRoom } from '../RoomProvider';
import { usePoseTimer } from './usePoseTimer';

/**
 * Hook for pose control handlers.
 * Centralizes all socket event emissions for pose management.
 * Schedule is managed via unified session state (not passed as parameter).
 */
export function usePoseControls() {
  const { socket } = useRoom();
  const { poseState } = usePoseTimer();

  const handlePlay = useCallback(() => {
    if (poseState.isUntimedBreak) {
      // End untimed break
      socket?.emit('break:end-untimed');
    } else if (poseState.isPaused) {
      // Resume paused timer
      socket?.emit('pose:resume');
    } else if (!poseState.isActive) {
      // Start pose - server uses schedule from unified session state
      socket?.emit('pose:start');
    }
  }, [socket, poseState.isActive, poseState.isUntimedBreak, poseState.isPaused]);

  const handlePause = useCallback(() => {
    // Pause calls an untimed break
    if (poseState.isActive && !poseState.isUntimedBreak) {
      socket?.emit('break:call-untimed');
    }
  }, [socket, poseState.isActive, poseState.isUntimedBreak]);

  const handleSkip = useCallback(() => {
    socket?.emit('pose:skip');
  }, [socket]);

  const handleStop = useCallback(() => {
    socket?.emit('pose:stop');
  }, [socket]);

  const handleRestart = useCallback(() => {
    socket?.emit('pose:restart');
  }, [socket]);

  const handleJumpTo = useCallback((index: number) => {
    socket?.emit('pose:jump-to', index);
  }, [socket]);

  // Computed control states
  const canPlay = !poseState.isActive || poseState.isPaused || poseState.isUntimedBreak;
  const canPause = poseState.isActive && !poseState.isPaused && !poseState.isUntimedBreak;

  return {
    handlePlay,
    handlePause,
    handleSkip,
    handleStop,
    handleRestart,
    handleJumpTo,
    canPlay,
    canPause,
  };
}
